import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { PlusIcon, EyeIcon, PencilIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

const MySwal = withReactContent(Swal);

// Modal Component for Add/Edit Feedback
const FeedbackModal = ({ isOpen, onClose, onSubmit, initialData, isEdit }) => {
    const [formData, setFormData] = useState(
        initialData || {
            name: "",
            rating: "",
            comment: "",
        }
    );
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = "Name is required";
        if (!formData.rating) newErrors.rating = "Rating is required";
        else if (isNaN(formData.rating) || formData.rating < 1 || formData.rating > 5)
            newErrors.rating = "Rating must be between 1 and 5";
        if (!formData.comment) newErrors.comment = "Comment is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setIsSubmitting(true);
            const data = {
                name: formData.name,
                rating: parseInt(formData.rating),
                comment: formData.comment,
            };
            await onSubmit(data);
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[300]">
            <div className="bg-[#0B2C3D] p-8 rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-300">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-extrabold text-white">{isEdit ? "Edit Feedback" : "Add Feedback"}</h3>
                    <button onClick={onClose} className="text-white hover:text-[#B39359] transition-colors duration-200">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>
                <form onSubmit={handleFormSubmit} className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-white">Name</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter name"
                        />
                        {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Rating (1-5)</label>
                        <input
                            type="number"
                            name="rating"
                            value={formData.rating}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter rating (1-5)"
                            min="1"
                            max="5"
                        />
                        {errors.rating && <p className="text-red-400 text-sm mt-1">{errors.rating}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Comment</label>
                        <textarea
                            name="comment"
                            value={formData.comment}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50 min-h-[100px]"
                            placeholder="Enter comment"
                        />
                        {errors.comment && <p className="text-red-400 text-sm mt-1">{errors.comment}</p>}
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-300 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-[#B39359] text-white hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                    Processing...
                                </>
                            ) : (
                                <>{isEdit ? "Update" : "Add"}</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Modal Component for Viewing Feedback Details
const FeedbackViewModal = ({ isOpen, onClose, feedback }) => {
    if (!isOpen || !feedback) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[300]">
            <div className="bg-[#0B2C3D] p-8 rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-300">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-extrabold text-white">Feedback Details</h3>
                    <button onClick={onClose} className="text-white hover:text-[#B39359] transition-colors duration-200">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-white">Name</label>
                        <p className="mt-1 text-white">{feedback.name || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Rating</label>
                        <p className="mt-1 text-white">{feedback.rating || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Comment</label>
                        <p className="mt-1 text-white">{feedback.comment || "N/A"}</p>
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="py-2 px-5 rounded-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-300"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function CustomerReviews() {
    const [feedbacks, setFeedbacks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [editFeedback, setEditFeedback] = useState(null);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const token = localStorage.getItem("token");

    // Fetch all feedbacks
    useEffect(() => {
        const fetchFeedbacks = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get("https://ligasos-laravel.ligasos.co/api/feedbacks", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setFeedbacks(response.data);
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Failed to fetch feedbacks. Please try again.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchFeedbacks();
    }, [token]);

    // Handle Add Feedback
    const handleAddFeedback = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.post("https://ligasos-laravel.ligasos.co/api/feedbacks", data, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setFeedbacks([...feedbacks, response.data.data]);
            setIsAddModalOpen(false);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Feedback added successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to add feedback.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Edit Feedback
    const handleEditFeedback = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.put(`https://ligasos-laravel.ligasos.co/api/feedbacks/${editFeedback.id}`, data, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setFeedbacks(feedbacks.map((f) => (f.id === editFeedback.id ? response.data.data : f)));
            setIsEditModalOpen(false);
            setEditFeedback(null);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Feedback updated successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to update feedback.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Delete Feedback
    const handleDeleteFeedback = async (id) => {
        const result = await MySwal.fire({
            title: "Are you sure?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#B39359",
            cancelButtonColor: "#dc2626",
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                await axios.delete(`https://ligasos-laravel.ligasos.co/api/feedbacks/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setFeedbacks(feedbacks.filter((f) => f.id !== id));
                MySwal.fire({
                    icon: "success",
                    title: "Deleted",
                    text: "Feedback deleted successfully!",
                    confirmButtonColor: "#B39359",
                    timer: 1500,
                    showConfirmButton: false,
                });
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: err.response?.data?.message || "Failed to delete feedback.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Handle View Feedback
    const handleViewFeedback = async (id) => {
        setIsLoading(true);
        try {
            const response = await axios.get(`https://ligasos-laravel.ligasos.co/api/feedbacks/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSelectedFeedback(response.data);
            setIsViewModalOpen(true);
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to fetch feedback details.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section className="p-8 min-h-screen">
            <div className="">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-4xl font-extrabold text-[#0B2C3D] tracking-tight">Customer Reviews</h2>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        disabled={isLoading}
                        className="flex items-center gap-2 bg-[#B39359] text-white font-semibold py-3 px-6 rounded-lg hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 shadow-md"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Add Feedback
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B39359]"></div>
                    </div>
                ) : feedbacks.length === 0 ? (
                    <p className="text-center text-gray-500 text-lg font-medium">No feedback found.</p>
                ) : (
                    <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-[#0B2C3D] sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Rating</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Comment</th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold text-white uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {feedbacks.map((feedback, index) => (
                                    <tr
                                        key={feedback.id}
                                        className={`hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                                    >
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{feedback.name || "N/A"}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{feedback.rating || "N/A"}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D] truncate max-w-xs">
                                            {feedback.comment || "N/A"}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => handleViewFeedback(feedback.id)}
                                                    className="p-2 text-[#0B2C3D] hover:bg-[#0B2C3D] hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <EyeIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditFeedback(feedback);
                                                        setIsEditModalOpen(true);
                                                    }}
                                                    className="p-2 text-[#0B2C3D] hover:bg-[#0B2C3D] hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <PencilIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteFeedback(feedback.id)}
                                                    className="p-2 text-red-600 hover:bg-red-600 hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modals */}
            <FeedbackModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSubmit={handleAddFeedback}
                isEdit={false}
            />
            <FeedbackModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEditFeedback(null);
                }}
                onSubmit={handleEditFeedback}
                initialData={editFeedback}
                isEdit={true}
            />
            <FeedbackViewModal
                isOpen={isViewModalOpen}
                onClose={() => {
                    setIsViewModalOpen(false);
                    setSelectedFeedback(null);
                }}
                feedback={selectedFeedback}
            />
        </section>
    );
}